import 'react-toastify/dist/ReactToastify.css';
import './styles/fonts.css';
import { ToastContainer } from 'react-toastify';
import * as React from 'react';
import { useEffect } from 'react';
import * as ReactDOM from 'react-dom/client';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeModeProvider, useThemeMode } from 'context/themeContext';
import DApp from './DApp';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import WalletAdapterProvider from 'providers/WalletAdapterProvider';
import { UserProvider } from 'context/userContext';
import { notifySlackSuccess } from 'utils/slack/slackNotifier';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SEGMENT_EVENTS } from 'data/segment-event';

/*// Initialize Sentry
Sentry.init({
	dsn: 'https://dd796496386e4b9fcf6c9d3995ae3062@o4510238510415872.ingest.de.sentry.io/4510238513561680',
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
		// Send console.log, console.warn, and console.error calls as logs to Sentry
		Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
	],
	// Tracing
	tracesSampleRate: 1.0, // Capture 100% of the transactions
	// Only propagate traces to backends that support Sentry tracing headers
	// Commented out to prevent CORS issues - we're manually creating spans instead
	tracePropagationTargets: [],
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
	// Enable logs to be sent to Sentry
	enableLogs: !window.location.hostname.includes('localhost'),
	// Set environment based on domain
	environment: window.location.hostname.includes('localhost')
		? 'development'
		: window.location.hostname,
});*/

// Create React Query client
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
			staleTime: 30000, // 30 seconds
		},
	},
});

const rootElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootElement!);

const CLARITY_DOMAIN_MAP: any = {
	'https://atlas.rndm.io': 'txc8i3rh8c',
};

const RootWithTheme = () => {
	const { mode } = useThemeMode();

	return (
		<>
			<DApp />
			{process.env.REACT_APP_DEV_TOOL && (
				<ReactQueryDevtools initialIsOpen />
			)}
			<ToastContainer style={{ fontSize: 14 }} theme={mode} />
		</>
	);
};

const Root = () => {
	useEffect(() => {
		const CLARITY_KEY: string = CLARITY_DOMAIN_MAP[window.location.origin];

		if (CLARITY_KEY) {
			const script = document.createElement('script');
			script.innerHTML = `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${CLARITY_KEY}");`;

			document.head.appendChild(script);

			return () => {
				document.head.removeChild(script);
			};
		}
		notifySlackSuccess({
			title: SEGMENT_EVENTS.OPEN_WEBSITE,
			details: ``,
			address: '',
		});
	}, []);

	return (
		<>
			<QueryClientProvider client={queryClient}>
				<WalletAdapterProvider>
					<UserProvider>
						<ThemeModeProvider>
							<CssBaseline />
							<RootWithTheme />
						</ThemeModeProvider>
					</UserProvider>
				</WalletAdapterProvider>
			</QueryClientProvider>
		</>
	);
};

root.render(<Root />);
